import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarPlus } from 'lucide-react';
import '../components/aiinterview/aiinterview.css';
import '../components/scheduling/scheduling.css';
import { browserTz, schedulingAPI } from '../lib/schedulingApi';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import InterviewsTab from '../components/scheduling/InterviewsTab';
import PoolsTab from '../components/scheduling/PoolsTab';
import AvailabilityTab from '../components/scheduling/AvailabilityTab';
import ScheduleDialog from '../components/scheduling/ScheduleDialog';
import { useCalendarReturnToast } from '../components/scheduling/CalendarConnections';

const TABS = [['interviews', 'Interviews'], ['pools', 'Interviewer pools'], ['availability', 'My availability & calendar']];

export default function SchedulingPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'interviews';
  const [rows, setRows] = useState(null);
  const [pools, setPools] = useState(null);
  const [team, setTeam] = useState(null);
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  useCalendarReturnToast(params, setParams);

  const loadRows = useCallback(() => schedulingAPI.list().then(({ data }) => setRows(data)).catch(() => { toast.error('Could not load interviews'); setRows([]); }), []);
  const loadPools = useCallback(() => Promise.all([schedulingAPI.pools(), schedulingAPI.team()])
    .then(([p, t]) => { setPools(p.data); setTeam(t.data); }).catch(() => setPools([])), []);
  const loadMe = useCallback(() => schedulingAPI.me().then(async ({ data }) => {
    if (data.profile.is_default) {
      try { data.profile = (await schedulingAPI.saveAvailability({ ...data.profile, timezone: browserTz() })).data; } catch (e) { /* keep defaults */ }
    }
    setMe(data);
  }).catch(() => {}), []);
  useEffect(() => { loadRows(); loadPools(); loadMe(); }, [loadRows, loadPools, loadMe]);

  return (
    <div className="iv iv-rpage" data-testid="scheduling-page">
      <div className="iv-rhead">
        <div>
          <div className="iv-eyebrow">Phase 2 · Human interviews</div>
          <h1 className="iv-rtitle">Interview Scheduling</h1>
          <p className="iv-sub" style={{ maxWidth: 640 }}>Book interviews with your team. Candidates pick from real free slots, interviewers share the load fairly, and everyone gets invites and reminders.</p>
        </div>
        <button type="button" className="iv-btn iv-btn-cta" onClick={() => setOpen(true)} disabled={!pools?.length} title={!pools?.length ? 'Create an interviewer pool first' : undefined} data-testid="schedule-interview-btn"><CalendarPlus size={16} /> Schedule interview</button>
      </div>
      {pools && !pools.length && <div className="iv-note is-warn" style={{ marginBottom: '1rem' }} data-testid="no-pools-note">Start by creating an interviewer pool on the Interviewer pools tab.</div>}
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList className="flex-wrap h-auto" data-testid="scheduling-tabs">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`sched-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <div style={{ marginTop: '1rem' }}>
          <TabsContent value="interviews"><InterviewsTab rows={rows} reload={loadRows} /></TabsContent>
          <TabsContent value="pools"><PoolsTab pools={pools} team={team} reload={loadPools} /></TabsContent>
          <TabsContent value="availability"><AvailabilityTab me={me} reload={() => { loadMe(); loadPools(); }} /></TabsContent>
        </div>
      </Tabs>
      <ScheduleDialog open={open} onOpenChange={setOpen} pools={pools} onCreated={() => { loadRows(); loadPools(); }} />
    </div>
  );
}
